import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * The Leader's Guide to Generative AI e-book's own page.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/leaders-guide-to-generative-ai/`), sent as five
 * screenshots on 2026-08-28 with *"referans alarak ilgili sayfayı tastamam
 * şekilde, aynısını, tıpkısını tasarla"*. Band for band that page's: the
 * two-column hero with the tunnel artwork beside it, the grey "Why Download This
 * Ebook?" card with the pair of cover mockups and three bolded promises, the
 * "What You'll Learn" card with the stacked copies and the five lessons, and the
 * centred "Don't Miss Out!" ask. The copy is that page's copy, verbatim.
 *
 * Sixth e-book page after Transform Better, Agentic AI, Sustainability
 * Innovation, Measurement and AI in Fashion Retail, and it follows
 * `ebook-ai-in-fashion-retail.ts` in every place the reference and this site
 * disagree — the same three deliberate departures, for the same reasons:
 *
 *  * **The eyebrow is the site's own**, over the copy column, rather than
 *    digitopia.co's small left-pinned "REDEFINING WHAT'S POSSIBLE IN BUSINESS"
 *    label. Every other hero here is that shape, and the words are unchanged.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean the
 *    page's only call to action silently drops every lead. The `check` below
 *    says so, as the sibling pages' do.
 *  * **A testimonial closes the page**, which the reference does not have — it
 *    is what the sibling e-book pages do, asked for on 2026-08-28 (*"en aşağıda
 *    önceden konuştuğumuz gibi testimonial template ini kullanalım"*). The card
 *    is Kordsa's: of the twenty in `content/testimonials.ts` it is the one whose
 *    words are about **AI** — being guided "to focus on critical areas like AI
 *    long before it became a major topic" — which is this book's own subject.
 *    Its words are the Hall of Fame's; that file is where a change to them
 *    belongs.
 *
 * **The five "What You'll Learn" lessons are flattened by one level.** The
 * reference nests each of them over three lines — the lesson, what it means, and
 * an "Example:" naming the company. A card's `points` is a flat list of strings,
 * and the nesting carried no information the sentence cannot: each point here is
 * the lesson, then what it means, then the example, in that order and with that
 * company's name intact.
 */
export const ebookGenerativeAi: WrittenPage = {
  navLabel: 'The Leader’s Guide to Generative AI',
  title: 'The Leader’s Guide to Generative AI',
  description:
    'Ready to harness the power of Generative AI? Actionable insights and real-world examples from Airbus, Sephora, DHL and Spotify. Download the e-book from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Redefining What’s Possible in Business',
      headline: 'The Leader’s Guide to Generative AI',
      body: [
        'Ready to harness the power of Generative AI? Our comprehensive ebook offers actionable '
          + 'insights and real-world examples to help you innovate and stay competitive.',
      ],
      ctas: [{ label: 'Download', href: LINKS.generativeAiEbookPdf }],
      photo: {
        src: '/photo/generative-ai-ebook-artwork.webp',
        alt:
          'The e-book’s cover artwork: a long-exposure tunnel of magenta and green light '
          + 'streaking towards a point in the distance.',
      },
      // The artwork is square and the site's hero box is 3:2, which would take
      // the top and bottom off the tunnel.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE ARTWORK IS 768–900px WIDE. Those are the largest copies of the '
        + 'cover artwork and the two mockups anybody has sent; every other hero on '
        + 'the site is 2560px, so this one is painted at roughly its own width on a '
        + 'laptop with nothing left over for a retina screen. If bigger exports '
        + 'exist wherever the covers were made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/generative-ai-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'Why Download This Ebook?',
      points: [
        'Practical Strategies: Learn how to implement Generative AI across various business '
          + 'functions, from product development to customer service.',
        'Real-World Examples: Discover how industry leaders like Airbus, Sephora, DHL, and '
          + 'Spotify are leveraging AI to drive growth.',
        'Actionable Insights: Get step-by-step guidance on integrating AI technologies and '
          + 'fostering a culture of innovation.',
      ],
      ctas: [{ label: 'Download the eBook', href: LINKS.generativeAiEbookPdf }],
      photo: {
        src: '/photo/generative-ai-ebook-covers.webp',
        alt:
          'Two copies of The Leader’s Guide to Generative AI, one behind the other, on a white '
          + 'background.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'What You’ll Learn',
      points: [
        'Innovate Product Development: accelerate product design and create new variations with '
          + 'AI. Example: how Autodesk’s Generative Design tool helped Airbus reduce weight by '
          + '45%.',
        'Enhance Customer Experiences: deliver personalized content and improve customer '
          + 'engagement. Example: Sephora’s AI chatbot offering tailored beauty advice.',
        'Streamline Operations: automate processes and optimize resources. Example: DHL’s '
          + 'AI-driven logistics optimization.',
        'Drive Innovation with AI Insights: leverage data to generate strategic insights. '
          + 'Example: Spotify’s AI predictions enhancing music recommendations.',
        'Ensure Ethical AI Deployment: implement robust governance frameworks for responsible AI '
          + 'use. Example: Microsoft’s Fairlearn toolkit for AI fairness.',
      ],
      ctas: [{ label: 'Download the eBook', href: LINKS.generativeAiEbookPdf }],
      photo: {
        src: '/photo/generative-ai-ebook-stack.webp',
        alt:
          'A stack of printed copies of The Leader’s Guide to Generative AI seen from above.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Don’t Miss Out!',
      sub:
        'Download our free ebook today and start transforming your business with Generative AI.',
      cta: { label: 'Download the eBook', href: LINKS.generativeAiEbookPdf },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our customers say.',
      items: [
        {
          // The Hall of Fame's Kordsa card, word for word —
          // `content/testimonials.ts` is where these words are edited, and the
          // Turkish page carries its own saying of them.
          quote:
            'Digitopia’s DMI assessments have helped us create clear, actionable digital '
            + 'roadmaps while staying on track. Their insights have been invaluable, guiding us '
            + 'to focus on critical areas like AI long before it became a major topic. Digitopia '
            + 'is not just a partner—they play an essential role in shaping and driving our '
            + 'digital strategy.',
          source: 'Fatih Akar, Director of Global Information Technologies',
          company: 'Kordsa',
          logo: { name: 'Kordsa', src: '/logo/kordsacard.png' },
          portrait: { src: '/photo/portrait-fatih-akar.png', alt: 'Fatih Akar' },
        },
      ],
    },
  ],
}
